import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, RefreshCw, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REASON_LABELS = {
  honeypot: { label: 'Honeypot trap', color: '#dc2626' },
  rate_limit: { label: 'Rate limited', color: '#d97706' },
  recaptcha: { label: 'reCAPTCHA failed', color: '#7c3aed' },
  speed_trap: { label: 'Speed trap (too fast)', color: '#0891b2' },
  'email_quality:disposable': { label: 'Throwaway email', color: '#be185d' },
  'email_quality:gibberish': { label: 'Gibberish email/name', color: '#be185d' },
  'email_quality:link_in_name': { label: 'Link in name', color: '#be185d' },
  'email_quality:non_latin': { label: 'Suspicious characters', color: '#be185d' },
  'email_quality:name_too_long': { label: 'Name too long', color: '#be185d' }
};

const FORM_LABELS = {
  contact: 'Contact form',
  subscribe: 'Newsletter popup',
  air_academy: 'Air Academy interest',
  waiver: 'Waiver'
};

const formatWhen = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const SpamTab = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [digestStatus, setDigestStatus] = useState('');
  const [sendingDigest, setSendingDigest] = useState(false);

  const sendTestDigest = async () => {
    setSendingDigest(true);
    setDigestStatus('');
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/spam-digest/test`, {}, { withCredentials: true });
      if (data.sent) {
        setDigestStatus('✅ Test digest sent to Zapier — check your Zap history / email.');
      } else if (data.reason === 'not_configured') {
        setDigestStatus('⚠️ No digest webhook configured yet. Create a Zapier "Catch Hook" Zap and add its URL as ZAPIER_SPAM_DIGEST_WEBHOOK_URL in your Secrets.');
      } else {
        setDigestStatus('❌ Webhook send failed — double-check the Zapier URL in your Secrets.');
      }
    } catch (err) {
      setDigestStatus('❌ Could not send test digest. Please try again.');
    } finally {
      setSendingDigest(false);
    }
  };

  const fetchLog = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/spam-log`, { withCredentials: true });
      setEntries(response.data);
    } catch (err) {
      setError(err.response?.status === 401 || err.response?.status === 403
        ? 'Not authorized — please log in as admin.'
        : 'Could not load the spam log. Try again shortly.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="chat-tab" data-testid="admin-spam-tab">
      <div className="chat-tab-header">
        <div>
          <h2><Shield size={22} /> Spam Shield</h2>
          <p className="chat-tab-sub">Blocked bot submissions across all forms — honeypot catches, rate-limit blocks, and failed reCAPTCHA checks.</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="chat-refresh-btn" onClick={sendTestDigest} disabled={sendingDigest} data-testid="spam-test-digest-btn">
            <Send size={16} /> {sendingDigest ? 'Sending…' : 'Send Test Digest'}
          </button>
          <button className="chat-refresh-btn" onClick={fetchLog} data-testid="spam-refresh-btn">
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </div>

      {digestStatus && <p className="chat-tab-status" data-testid="spam-digest-status">{digestStatus}</p>}

      {loading && <p className="chat-tab-status">Loading…</p>}
      {error && <p className="chat-tab-status chat-tab-error">{error}</p>}
      {!loading && !error && entries.length === 0 && (
        <p className="chat-tab-status">No blocked attempts yet — the shield is up and quiet. Blocked bots will appear here.</p>
      )}

      {!loading && !error && entries.length > 0 && (
        <div className="spam-log-table">
          <div className="spam-log-row spam-log-head">
            <span>When</span>
            <span>Form</span>
            <span>Reason</span>
            <span>Email</span>
            <span>IP</span>
          </div>
          {entries.map((e, i) => {
            const reason = REASON_LABELS[e.reason] || { label: e.reason, color: '#6b7280' };
            return (
              <div className="spam-log-row" key={`${e.created_at}-${i}`}>
                <span>{formatWhen(e.created_at)}</span>
                <span>{FORM_LABELS[e.form] || e.form}</span>
                <span className="spam-reason" style={{ color: reason.color }}>
                  {reason.label}{typeof e.score === 'number' ? ` (score ${e.score})` : ''}
                </span>
                <span className="spam-email">{e.email || '—'}</span>
                <span className="spam-ip">{e.ip || '—'}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SpamTab;
